import type { DatePickerConfig, FormConfig, SelectConfig, SmallerThanValidValidatorConfig } from '@common';
import { FormDefaultAttributes } from '@common';
import { StatisticsKind } from '../enums';
import { PeriodBoundary } from '../../../models';

export const needYieldField = (kind?: StatisticsKind): boolean => {
  return (
    kind === StatisticsKind.YIELD_APPLIED_DOSE ||
    kind === StatisticsKind.YIELD_ZONE_STRENGTH ||
    kind === StatisticsKind.YIELD_STEM_NUMBER ||
    kind === StatisticsKind.YIELD_SOIL_PARAMETER
  );
};

export const getStatisticsWizardParcelFormConfig = (kind?: StatisticsKind): FormConfig => ({
  name: 'statisticsWizardParcelForm',
  attributes: {
    ...FormDefaultAttributes.getFormDefaultAttributes()
  },
  children: [
    {
      name: 'yield',
      component: 'select',
      attributes: {
        label: 'statistics.wizard.label.yield',
        itemTitle: 'name',
        itemValue: 'value',
        returnObject: true
      },
      settings: {
        visible: needYieldField(kind),
        needTranslate: true,
        grid: {
          cols: 12,
          md: 6
        },
        hideGridSlotWhenInvisible: true
      },
      validators: [
        {
          validator: 'required'
        },
        {
          validator: 'yieldZoneLevel',
          settings: {
            invalidMessage: 'statistics.wizard.label.yieldWarningMessage'
          }
        }
      ]
    } as SelectConfig,
    {
      name: 'zoneStrength',
      component: 'select',
      attributes: {
        label: 'statistics.wizard.label.zoneStrength',
        itemTitle: 'name',
        itemValue: 'value',
        returnObject: true
      },
      settings: {
        visible: kind === StatisticsKind.YIELD_ZONE_STRENGTH,
        needTranslate: true,
        readonly: true,
        grid: {
          cols: 12,
          md: 6
        },
        validation: {
          onInput: false,
          onBlur: false
        },
        hideGridSlotWhenInvisible: true
      },
      validators: [
        {
          validator: 'required'
        },
        {
          validator: 'sourceYear',
          settings: {
            invalidMessage: 'statistics.wizard.label.statisticsSourceYearWarningMessage'
          }
        }
      ]
    } as SelectConfig,
    {
      name: 'stemNumber',
      component: 'select',
      attributes: {
        label: 'statistics.wizard.label.stemNumber',
        itemTitle: 'name',
        itemValue: 'value',
        returnObject: true,
        multiple: true,
        clearable: true,
        chips: true,
        closableChips: true,
        persistentHint: true
      },
      settings: {
        visible: kind === StatisticsKind.YIELD_STEM_NUMBER,
        needTranslate: true,
        grid: {
          cols: 12,
          md: 6
        },

        hideGridSlotWhenInvisible: true
      },
      validators: [
        {
          validator: 'required'
        }
      ]
    } as SelectConfig,
    {
      name: 'soilSample',
      component: 'select',
      attributes: {
        label: 'statistics.wizard.label.soilSample',
        itemTitle: 'name',
        itemValue: 'value',
        returnObject: true
      },
      settings: {
        visible: kind === StatisticsKind.YIELD_SOIL_PARAMETER,
        needTranslate: true,
        grid: {
          cols: 12,
          md: 6
        },
        validation: {
          onBlur: false
        },
        readonly: true,
        hideGridSlotWhenInvisible: true
      },
      validators: [
        {
          validator: 'required',
          settings: {
            invalidMessage: 'statistics.wizard.label.soilSampleMissing'
          }
        }
      ]
    } as SelectConfig,
    {
      name: 'appliedDose',
      component: 'select',
      attributes: {
        label: 'statistics.wizard.label.appliedDose',
        itemTitle: 'name',
        itemValue: 'value',
        multiple: true,
        clearable: true,
        chips: true,
        closableChips: true,
        returnObject: true,
        persistentHint: true
      },
      settings: {
        visible: kind === StatisticsKind.YIELD_APPLIED_DOSE,
        needTranslate: true,
        grid: {
          cols: 12,
          md: 6
        },
        hideGridSlotWhenInvisible: true
      },
      validators: [
        {
          validator: 'required'
        },
        {
          validator: 'yieldZoneLevel',
          settings: {
            invalidMessage: 'statistics.wizard.label.yieldWarningMessage'
          }
        }
      ]
    } as SelectConfig,
    {
      name: 'fromDate',
      component: 'datepicker',
      attributes: {
        label: 'statistics.wizard.label.fromDate',
        displayFormat: 'yyyy.MM.dd.',
        format: 'yyyy-MM-dd',
        max: new Date(),
        clearable: true,
        closeCalendarOnPick: true
      },
      settings: {
        visible: kind === StatisticsKind.SOIL_TEMPERATURE,
        needTranslate: true,
        grid: {
          cols: 12,
          md: 6
        },
        validation: {
          onBlur: true,
          onInput: true
        },
        hideGridSlotWhenInvisible: true
      },
      relatedElements: ['statisticsWizardParcelForm.toDate'],
      validators: [
        {
          validator: 'required'
        },
        {
          validator: 'smallerThan',
          settings: {
            otherFieldName: 'toDate',
            invalidMessage: 'statistics.form.validator.smallerThan.invalidMessage',
            needTranslate: true,
            allowEqual: false
          }
        } as SmallerThanValidValidatorConfig,
        {
          validator: 'soilTemperaturePeriodDate',
          settings: {
            otherFieldName: 'toDate',
            periodBoundary: PeriodBoundary.START,
            needTranslate: true
          }
        }
      ]
    } as DatePickerConfig,
    {
      name: 'toDate',
      component: 'datepicker',
      attributes: {
        label: 'statistics.wizard.label.toDate',
        displayFormat: 'yyyy.MM.dd.',
        format: 'yyyy-MM-dd',
        max: new Date(),
        clearable: true,
        closeCalendarOnPick: true
      },
      settings: {
        visible: kind === StatisticsKind.SOIL_TEMPERATURE,
        needTranslate: true,
        grid: {
          cols: 12,
          md: 6
        },
        validation: {
          onBlur: true,
          onInput: true,
          validateParent: true
        },
        hideGridSlotWhenInvisible: true
      },
      relatedElements: ['statisticsWizardParcelForm.fromDate'],
      validators: [
        {
          validator: 'required'
        },
        {
          validator: 'greaterThan',
          settings: {
            otherFieldName: 'fromDate',
            invalidMessage: 'statistics.form.validator.greaterThan.invalidMessage',
            needTranslate: true,
            allowEqual: false
          }
        },
        {
          validator: 'soilTemperaturePeriodDate',
          settings: {
            otherFieldName: 'fromDate',
            periodBoundary: PeriodBoundary.END,
            needTranslate: true
          }
        }
      ]
    } as DatePickerConfig
  ]
});
